import { useState, useMemo } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Search, X, Archive, Users, MessageCirclePlus } from "lucide-react"
import type { Conversation, ChatFilter } from "./types"
import { ChatFilterChips } from "./chat-filter-chips"
import { ChatListTile } from "./chat-list-tile"
import { ArchivedChatsPage } from "./archived-chats-page"
import { NewChatPage } from "./new-chat-page"

interface ChatListPanelProps {
  conversations: Conversation[]
  selectedId: string | null
  onSelect: (c: Conversation) => void
  onNewChat?: () => void
  onArchive?: (id: string) => void
  onPin?: (id: string) => void
  onMute?: (id: string) => void
  onDelete?: (id: string) => void
  onExitGroup?: (id: string) => void
  onFavorite?: (id: string) => void
  onMarkAsRead?: (id: string) => void
  onClearChat?: (id: string, opts: { keepStarred: boolean }) => void
  onBlock?: (id: string) => void
  /**
   * Is this conversation's peer blocked? Asked per row rather than passed as a
   * flag on `Conversation`, because a block is between PEOPLE — it lives on the
   * caller's block list, not on the thread record the list renders.
   */
  isConversationBlocked?: (c: Conversation) => boolean
  onUnblock?: (id: string) => void
  onViewContact?: (id: string) => void
}

export function ChatListPanel({ conversations, selectedId, onSelect, onNewChat, onArchive, onPin, onMute, onDelete, onExitGroup, onFavorite, onMarkAsRead, onClearChat, onBlock, isConversationBlocked, onUnblock, onViewContact }: ChatListPanelProps) {
  const t = useTranslation()
  const [filter, setFilter] = useState<ChatFilter>("all")
  const [search, setSearch] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [showNewChat, setShowNewChat] = useState(false)

  const activeConvs = useMemo(() => conversations.filter(c => !c.isArchived), [conversations])
  const archivedConvs = useMemo(() => conversations.filter(c => c.isArchived), [conversations])

  const filtered = useMemo(() => {
    let list = activeConvs
    if (filter === "unread") list = list.filter(c => c.unreadCount > 0)
    else if (filter === "groups") list = list.filter(c => c.isGroup)
    else if (filter === "favorites") list = list.filter(c => c.isFavorite)
    else if (filter === "pinned") list = list.filter(c => c.isPinned)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(c => c.name.toLowerCase().includes(q) || c.lastMessage.toLowerCase().includes(q))
    }
    return list
  }, [activeConvs, filter, search])

  if (showArchived) {
    return (
      <ArchivedChatsPage
        archivedChats={archivedConvs}
        onUnarchive={(id) => onArchive?.(id)}
        onDelete={(id) => onDelete?.(id)}
        onFavorite={(id) => onFavorite?.(id)}
        onBlock={(id) => { onBlock?.(id); setShowArchived(false) }}
        onSelectChat={(c) => { setShowArchived(false); onSelect(c) }}
        onClose={() => setShowArchived(false)}
      />
    )
  }

  if (showNewChat) {
    return (
      <NewChatPage
        contacts={activeConvs.map(c => ({ id: c.id, name: c.name, imageUrl: c.imageUrl, subtitle: c.isGroup ? t('presence.group') : c.lastMessage }))}
        onClose={() => setShowNewChat(false)}
        onSelectContact={(contact) => {
          const conv = activeConvs.find(c => c.id === contact.id)
          if (conv) { setShowNewChat(false); onSelect(conv) }
        }}
      />
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        {isSearching ? (
          <div className="flex items-center gap-2 flex-1">
            <button onClick={() => { setIsSearching(false); setSearch("") }} className="p-1 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-5 h-5" />
            </button>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t("presence.searchChats")}
              className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground outline-none"
            />
          </div>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-foreground">{t("presence.chats")}</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsSearching(true)}
                className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title={t("common.search")}
              >
                <Search className="w-4.5 h-4.5" />
              </button>
              <button
                onClick={() => setShowArchived(true)}
                className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title={t("presence.archivedChats")}
              >
                <Archive className="w-4.5 h-4.5" />
              </button>
              <button
                onClick={onNewChat}
                className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title={t("presence.newChat")}
              >
                <MessageCirclePlus className="w-4.5 h-4.5" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Filter chips */}
      {!isSearching && (
        <ChatFilterChips current={filter} onChange={setFilter} />
      )}

      {/* Chat list */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* Pinned section header */}
        {filter === "all" && !search && filtered.some(c => c.isPinned) && (
          <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 bg-muted/30">
            {t("presence.pinned")}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-8 gap-3">
            {filter === "groups" ? (
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Users className="w-5 h-5 text-muted-foreground" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Search className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {search ? `${t("presence.noResults")} "${search}"` : `${t("presence.noChats")}`}
            </p>
          </div>
        ) : (
          <>
            {filter === "all" && !search
              ? (
                <>
                  {filtered.filter(c => c.isPinned).map(conv => (
                    <ChatListTile key={conv.id} conversation={conv} isSelected={selectedId === conv.id} onClick={() => onSelect(conv)} onArchive={onArchive} onPin={onPin} onMute={onMute} onDelete={onDelete} onExitGroup={onExitGroup} onFavorite={onFavorite} onMarkAsRead={onMarkAsRead} onClearChat={onClearChat} onBlock={onBlock} isBlocked={isConversationBlocked?.(conv)} onUnblock={onUnblock} onViewContact={onViewContact} />
                  ))}
                  {filtered.some(c => c.isPinned) && filtered.some(c => !c.isPinned) && (
                    <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 bg-muted/30">
                      {t("presence.allChats")}
                    </div>
                  )}
                  {filtered.filter(c => !c.isPinned).map(conv => (
                    <ChatListTile key={conv.id} conversation={conv} isSelected={selectedId === conv.id} onClick={() => onSelect(conv)} onArchive={onArchive} onPin={onPin} onMute={onMute} onDelete={onDelete} onExitGroup={onExitGroup} onFavorite={onFavorite} onMarkAsRead={onMarkAsRead} onClearChat={onClearChat} onBlock={onBlock} isBlocked={isConversationBlocked?.(conv)} onUnblock={onUnblock} onViewContact={onViewContact} />
                  ))}
                </>
              )
              : filtered.map(conv => (
                <ChatListTile key={conv.id} conversation={conv} isSelected={selectedId === conv.id} onClick={() => onSelect(conv)} onArchive={onArchive} onPin={onPin} onMute={onMute} onDelete={onDelete} onExitGroup={onExitGroup} onFavorite={onFavorite} onMarkAsRead={onMarkAsRead} onClearChat={onClearChat} onBlock={onBlock} isBlocked={isConversationBlocked?.(conv)} onUnblock={onUnblock} onViewContact={onViewContact} />
              ))
            }
          </>
        )}

        {/* Archived chats are reached via the header archive icon — no inline
            row in the All list (kept the list clean per product decision). */}
      </div>
    </div>
  )
}
